import React from 'react';
import { Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, SyncOutlined } from '@ant-design/icons';
import {
    ROLE_COLORS,
    USER_STATUS_COLORS,
    FEST_STATUS_COLORS,
    RESULT_STATUS_COLORS,
    EVENT_TYPE_COLORS,
    GRADE_COLORS,
    SCORE_STATUS_COLORS,
    SCORE_STATUS_LABELS,
    EVENT_STATUS_TAG_COLOR,
    EVENT_STATUS_LABEL,
} from '../../constants/colors';

export type StatusTagType = 'role' | 'userStatus' | 'fest' | 'event' | 'result' | 'eventType' | 'grade' | 'score';

const COLOR_MAPS: Record<StatusTagType, Record<string, string>> = {
    role: ROLE_COLORS,
    userStatus: USER_STATUS_COLORS,
    fest: FEST_STATUS_COLORS,
    event: EVENT_STATUS_TAG_COLOR,
    result: RESULT_STATUS_COLORS,
    eventType: EVENT_TYPE_COLORS,
    grade: GRADE_COLORS,
    score: SCORE_STATUS_COLORS,
};

const LABEL_MAPS: Partial<Record<StatusTagType, Record<string, string>>> = {
    event: EVENT_STATUS_LABEL,
    score: SCORE_STATUS_LABELS,
};

const SCORE_ICONS: Record<string, React.ReactNode> = {
    SCORED: <CheckCircleOutlined />,
    ABSENT: <CloseCircleOutlined />,
    PENDING: <ClockCircleOutlined />,
    PARTIAL: <SyncOutlined />,
};

/** Turns SNAKE_CASE enum values into Title Case ("ITEM_COORDINATOR" → "Item Coordinator"). */
const titleCase = (value: string) =>
    value
        .toLowerCase()
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

interface StatusTagProps {
    type: StatusTagType;
    value: string | null | undefined;
    /** Override the derived label. */
    label?: React.ReactNode;
    className?: string;
}

/**
 * The only sanctioned way to render a status/role/grade Tag.
 * Colors come from constants/colors.ts so every page shows the same
 * color for the same value.
 */
const StatusTag: React.FC<StatusTagProps> = ({ type, value, label, className }) => {
    if (!value) return <Tag className={className}>N/A</Tag>;
    const color = COLOR_MAPS[type][value] || 'default';
    const derivedLabel = label ?? LABEL_MAPS[type]?.[value] ?? (type === 'grade' ? value : titleCase(value));
    const icon = type === 'score' ? SCORE_ICONS[value] : undefined;
    return (
        <Tag color={color} icon={icon} className={className}>
            {derivedLabel}
        </Tag>
    );
};

export default StatusTag;
